import type {BannerDataText} from '@core-client/ads/banner/data/BannerDataText';
import type {AdBannerEventType} from '@core-client/ads/banner/AdBannerEventType';
import {BannerPlaceListenersProxy} from '../listener/BannerPlaceListenersProxy';
import {BannerRequestId} from '../transport/BannerRequestId';
import type {ISimpleBannerOptions} from '../variant/SimpleBanner';

import {BaseBannerHook, type IBannerHookSettings} from './BaseBannerHook';
import type {IBannerJson} from './IBannerJson';
import type {HookBannerParams} from './HookBannerParams';

export class BannerWithOptionsHook extends BaseBannerHook<IBannerJson> {
    protected adWaitTime: number = 0;
    protected adWaitTimeoutId: ReturnType<typeof setTimeout> | undefined;
    protected autoCloseTime: number = 0;
    protected autoCloseTimeoutId: ReturnType<typeof setTimeout> | undefined;
    protected directBannerNode: Element | undefined;
    protected directBannerNodeChecked: boolean = false;

    constructor(settings: IBannerHookSettings<IBannerJson>) {
        super(settings);
    }

    override deactivate() {
        super.deactivate();
        clearTimeout(this.adWaitTimeoutId);
        clearTimeout(this.autoCloseTimeoutId);
        if (this.directBannerNode) {
            this.directBannerNode.innerHTML = '';
        }
    }

    override prepareBannerOptions(options: ISimpleBannerOptions): ISimpleBannerOptions {
        options.hideContainerOnHide = true;
        return options;
    }

    protected override readAdditionalParams(json: IBannerJson, params: HookBannerParams) {
        const { adWaitTime, autoCloseTime } = json;
        if (adWaitTime && adWaitTime > 0) {
            // adWaitTime задано в секундах
            this.adWaitTime = adWaitTime * 1000;
            this.adWaitTimeoutId = setTimeout(this.onAdWaitTimeout, this.adWaitTime);
        }
        if (autoCloseTime && autoCloseTime > 0) {
            // autoCloseTime задано в секундах
            this.autoCloseTime = autoCloseTime * 1000;
        }
    }

    protected getDataForStat() {
        return new BannerRequestId('', this.bannerCfg.place, this.position, this.element)
                    .setBannerCfg(this.bannerCfg)
                    .setResult(this.bannerRequestResult);
    }

    protected getDirectBannerNode() {
        if (!this.directBannerNodeChecked) {
            if (this.bannerRequestResult && (this.bannerRequestResult as BannerDataText).getUsedDirectBannerNode) {
                this.directBannerNode = (this.bannerRequestResult as BannerDataText).getUsedDirectBannerNode();
            }
            this.directBannerNodeChecked = true;
        }
        return this.directBannerNode;
    }

    protected checkBannerFallback() {
        if (this.bannerRequestResult && (this.bannerRequestResult as BannerDataText).checkBannerFallback
            && (this.bannerRequestResult as BannerDataText).checkBannerFallback()) {
            this.dispatchAdFallback();
        }
    }

    protected dispatchAdClosed() {
        if (this.bannerCfg.callbacksEnabled) {
            BannerPlaceListenersProxy.getInstance().onAdClosed(this.getDataForStat());
        }
    }

    protected dispatchAdFallback() {
        if (this.bannerCfg.callbacksEnabled) {
            BannerPlaceListenersProxy.getInstance().onAdFallback(this.getDataForStat());
        }
    }

    protected dispatchAdLoaded() {
        if (this.bannerCfg.callbacksEnabled) {
            BannerPlaceListenersProxy.getInstance().onAdLoaded(this.getDataForStat());
        }
    }

    protected dispatchAdShown() {
        if (this.bannerCfg.callbacksEnabled) {
            BannerPlaceListenersProxy.getInstance().onAdShown(this.getDataForStat());
        }
    }

    protected dispatchAdWaitTimeout() {
        if (this.bannerCfg.callbacksEnabled) {
            BannerPlaceListenersProxy.getInstance().onAdWaitTimeout(this.getDataForStat());
        }
    }

    protected getAdLoadEventList(): AdBannerEventType[] {
        return [];
    }

    protected listenAdLoaded(stopListen?: boolean) {
        const elem = this.element;
        const eventList = this.getAdLoadEventList();
        const eventCount = eventList.length;
        if (elem && eventCount) {
            for (let i = 0; i < eventCount; i++) {
                if (stopListen) {
                    elem.removeEventListener(eventList[i], this.onAdLoaded);
                } else {
                    elem.addEventListener(eventList[i], this.onAdLoaded);
                }
            }
        }
    }

    protected unlistenAdLoaded() {
        this.listenAdLoaded(true);
    }

    protected onAdClosed = () => {
        this.deactivate();
        this.dispatchAdClosed();
    };

    protected handleAdLoaded() {
        this.unlistenAdLoaded();
        this.checkBannerFallback();
        this.dispatchAdLoaded();
    }

    protected onAdLoaded = () => {
        this.handleAdLoaded();
    };

    protected onAdShown = () => {
        this.scheduleAutoClose();
        this.dispatchAdShown();
    };

    protected onAdWaitTimeout = () => {
        // Баннер от Яндекса может встраиваться не в наш контейнер, а в конец страницы.
        // Пытаемся его найти. Если нашли, будем считать, что реклама отображается.
        const directBannerNode = this.getDirectBannerNode();
        if (directBannerNode) {
            if (this.autoCloseTime > 0) {
                if (this.autoCloseTime > this.adWaitTime) {
                    this.scheduleAutoClose(this.autoCloseTime - this.adWaitTime);
                } else {
                    this.deactivate();
                }
            }
            return;
        }
        this.dispatchAdWaitTimeout();
    };

    protected onAutoCloseTimeout = () => {
        this.deactivate();
    };

    protected scheduleAutoClose(timeout?: number) {
        clearTimeout(this.autoCloseTimeoutId);
        if (this.autoCloseTime > 0) {
            this.autoCloseTimeoutId = setTimeout(this.onAutoCloseTimeout, timeout || this.autoCloseTime);
        }
    }
}
